// Utilitários para o OAuth 2.0 `state` param usado no fluxo redirect do Discord SSO.
// O `state` carrega dois dados: um token CSRF aleatório (validado contra sessionStorage
// no retorno) e a URL pra onde o usuário deve voltar depois do login.

interface OAuthStatePayload {
  csrf: string;
  returnTo: string;
  locale?: string;
  // 'login' (default behaviour, omitted by existing callers) authenticates the user via this
  // provider. 'link' means the user is already authenticated and is connecting this provider
  // to their account from /dashboard/connections — the callback branches on this to call the
  // link endpoint instead of the login endpoint. 'desktop' is the RFC 8252 loopback handoff: the
  // C++ desktop client opened the browser at /auth/desktop, so on success the callback redirects
  // the browser to `redirectUri` (below) with the login-token instead of navigating in-app.
  mode?: 'login' | 'link' | 'desktop' | 'mobile';
  // Only set (and only trusted) when mode === 'desktop'. Validated once by /auth/desktop before
  // it's placed in the state, and re-validated by the callback with isSafeDesktopRedirect before
  // use — the state is attacker-controlled round-trip data, never trust it on its own.
  redirectUri?: string;
  // Only set (and only trusted) when mode === 'mobile'. The loopback port the mobile app is
  // listening on; re-validated by the callback with parseCbHttpPort before use, for the same
  // reason as redirectUri — the state is attacker-controlled round-trip data.
  cbHttpPort?: number;
}

export function generateCsrfToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function encodeOAuthState(payload: OAuthStatePayload): string {
  const json = JSON.stringify(payload);
  if (typeof window === 'undefined') return '';
  // base64url: remove padding e troca '+'/'/' por '-'/'_'
  return window.btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeOAuthState(state: string | null | undefined): Partial<OAuthStatePayload> | null {
  if (!state || typeof window === 'undefined') return null;
  try {
    const base64 = state.replace(/-/g, '+').replace(/_/g, '/');
    // restaura o padding que o base64url removeu
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = window.atob(padded);
    const parsed = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as Partial<OAuthStatePayload>;
  } catch {
    return null;
  }
}

// Aceita apenas paths same-origin (relativos começando com '/') pra previnir open redirect.
// Allowlist (em vez de blocklist): rejeita qualquer coisa que não seja um path relativo
// literal E que não resolva pra uma URL same-origin. Isso cobre variantes off-spec que
// outros frameworks tratam como protocol-relative — '//evil.com', '/\evil.com' (browsers
// normalizam '\' pra '/') e formas percent-encoded como '/%2F%2Fevil.com'.
export function isSafeReturnTo(path: unknown): path is string {
  if (typeof path !== 'string' || path.length === 0) return false;
  if (path[0] !== '/') return false;
  if (path[1] === '/' || path[1] === '\\') return false;

  // Decodifica uma vez pra pegar formas percent-encoded do mesmo ataque (ex: '/%2F%2Fevil.com'
  // vira '//evil.com'). decodeURIComponent lança em sequências malformadas — trata como inseguro.
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(path);
  } catch {
    return false;
  }
  if (decodedPath[1] === '/' || decodedPath[1] === '\\') return false;

  if (typeof window === 'undefined') return false;

  try {
    const resolved = new URL(path, window.location.origin);
    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
}

// O deep-link do cliente desktop leva o provider escolhido em `?provider=`, e /auth/desktop dispara
// esse provider sozinho no mount. Mandar uma falha de volta para essa mesma URL relança o fluxo
// imediatamente — o toast aparece e a página sai de baixo dele, virando um ping-pong entre o site e a
// tela do provider, sem o usuário nunca conseguir ler o motivo. No caminho de erro o param sai, e a
// página renderiza os botões com a mensagem do lado.
export function withoutAutoStartProvider(returnTo: string): string {
  if (typeof window === 'undefined') return returnTo;

  try {
    const resolved = new URL(returnTo, window.location.origin);
    if (!resolved.searchParams.has('provider')) return returnTo;
    resolved.searchParams.delete('provider');
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return returnTo;
  }
}
